import dayjs from "dayjs";
import { BenefitStorage } from "./BenefitStorage";
import { CatalogHelpers } from "shared/tools/CatalogHelpers";
import { Benefit } from "shared/tools/Benefit";
import { OMath } from "shared/utils/OMath";
import { Time } from "shared/tools/Time";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";

const orderKey = "gestor-order";
const lastCartUpdateKey = "last-cart-update";

const GIFT_TYPE = {
  // ===== Valores adicioniais =====
  ADDITION: 1,

  // ===== Forma de cobrança =====
  PRICE: 2,
  PERCENT: 4,

  // ===== Cobrar com base em =====
  CART: 8,
  DELIVERY: 16,

  // ===== Tipo de uso do cupom =====
  FIRST_PURCHASE_EVER: 32,
  N_USE: 64,
  FREE_TO_USE: 128,
  NEXT_PURCHARSE: 256,
  CASHBACK_USE: 512,
  CASHBACK_EARN: 1024,

  PRODUCT: 2048,
};
export class OrderStorage {
  static isRefreshing = false;

  static get orderTemplate() {
    return {
      cart: {
        catalog: {
          service: null,
          items: [],
          type: "catalog",
          quantity: 1,
          selected: true,
        },
        total: 0,
      },
      payment: { payers: [], total: 0 },
      delivery: { type: 1, address: null, total: null },
      gift: null,
      total: 0,
    };
  }

  static initOrder() {
    const orderTemplate = OrderStorage.orderTemplate;

    const unparsedData = JSON.stringify(orderTemplate);
    localStorage.setItem(orderKey, unparsedData);

    if (window) {
      window.dispatchEvent(
        new CustomEvent("orderUpdated", { detail: orderTemplate }),
      );
    }
  }

  static addItemToCart(cartItem) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items.push(cartItem);
    order.cart.total = CatalogHelpers.calculateSelectedItemPrice(
      order.cart.catalog,
    );

    if (order.gift && this.typeIs(order.gift, GIFT_TYPE.PRODUCT)) {
      const selectedBenefit = BenefitStorage.getSelectedBenefit();
      order.gift.target_id = CatalogHelpers.getFirstItemSkuOnSkuList(
        order.cart.catalog.items,
        selectedBenefit?.product_list || [],
      );
    }

    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static updateCartItem(cartItemIndex, cartItem) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items[cartItemIndex] = cartItem;
    order.cart.total = CatalogHelpers.calculateSelectedItemPrice(
      order.cart.catalog,
    );

    if (order.gift && this.typeIs(order.gift, GIFT_TYPE.PRODUCT)) {
      const selectedBenefit = BenefitStorage.getSelectedBenefit();
      order.gift.target_id = CatalogHelpers.getFirstItemSkuOnSkuList(
        order.cart.catalog.items,
        selectedBenefit?.product_list || [],
      );
    }

    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static removerCartItem(cartItemIndex) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items = order.cart.catalog.items.filter(
      (_, cIndex) => cIndex != cartItemIndex,
    );
    order.cart.total = CatalogHelpers.calculateSelectedItemPrice(
      order.cart.catalog,
    );

    if (order.gift && this.typeIs(order.gift, GIFT_TYPE.PRODUCT)) {
      const selectedBenefit = BenefitStorage.getSelectedBenefit();
      order.gift.target_id = CatalogHelpers.getFirstItemSkuOnSkuList(
        order.cart.catalog.items,
        selectedBenefit?.product_list || [],
      );
    }

    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static updateCartCatalogService(service) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.service = service;
    OrderStorage.setOrder(order);
  }

  static clearCartItems() {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items = [];
    order.cart.total = 0;
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static get paymentMethod() {
    const order = OrderStorage.getOrder();
    return order.payment.payers[0];
  }

  static getPaymentPayers() {
    const order = OrderStorage.getOrder();
    return order.payment.payers;
  }

  static getDelivery() {
    const order = OrderStorage.getOrder();
    return order.delivery;
  }

  static setDelivery(delivery) {
    const order = OrderStorage.getOrder();
    order.delivery = delivery;
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static resetDelivery() {
    const order = OrderStorage.getOrder();
    order.delivery = { type: 1, address: null, total: null };
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static setDeliveryType(type) {
    const order = OrderStorage.getOrder();
    order.delivery.type = type;
    OrderStorage.setOrder(order);
  }

  static setPaymentPayers(payers) {
    const order = OrderStorage.getOrder();
    order.payment.payers = [payers];
    OrderStorage.setOrder(order);
  }

  static setDeliveryAddress(address) {
    const order = OrderStorage.getOrder();
    order.delivery.address = address;
    OrderStorage.setOrder(order);
  }

  static setTotal(total) {
    const order = OrderStorage.getOrder();
    order.total = total;
    OrderStorage.setOrder(order);
  }

  static setObservation(observation) {
    const order = OrderStorage.getOrder();
    order.observation = observation;
    OrderStorage.setOrder(order);
  }

  static setChangeFor(changeFor) {
    const order = OrderStorage.getOrder();
    order.payment.payers[0].changeFor = changeFor;
    OrderStorage.setOrder(order);
  }

  static getCustomer() {
    const order = OrderStorage.getOrder();
    return order.payment?.payers?.[0]?.user;
  }

  static getOrder() {
    try {
      const orderRaw = localStorage.getItem(orderKey);
      if (!orderRaw) {
        return OrderStorage.initOrder();
      }

      return JSON.parse(orderRaw);
    } catch (err) {
      return null;
    }
  }

  static setOrder(data) {
    const unparsedData = JSON.stringify(data);

    OrderStorage.setLastUpdateCart();

    localStorage.setItem(orderKey, unparsedData);
    if (window) {
      window.dispatchEvent(new CustomEvent("orderUpdated", { detail: data }));
    }
  }

  static setLastUpdateCart() {
    const lastCartUpdate = localStorage.getItem(lastCartUpdateKey);

    const diff = OrderStorage.getLastUpdateCart();

    if (!lastCartUpdate || diff <= 60) OrderStorage.resetLastUpdateCart();
  }

  static resetLastUpdateCart() {
    const now = Time.getInstance();

    localStorage.setItem(lastCartUpdateKey, JSON.stringify(now));
  }

  static getLastUpdateCart() {
    const lastCartUpdate = localStorage.getItem(lastCartUpdateKey);

    if (!lastCartUpdate) return;

    const now = Time.getInstance();
    const lastCartUpdateParsed = Time.getInstance(JSON.parse(lastCartUpdate));

    const diff = now.diff(lastCartUpdateParsed, "minute");

    return diff;
  }

  static typeIs(gift, type) {
    return (gift.type & type) == type;
  }

  static getGift() {
    const order = OrderStorage.getOrder();
    return order.gift;
  }

  static setGift(gift) {
    const order = OrderStorage.getOrder();
    order.gift = gift;
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static setCustomer(customer) {
    const order = OrderStorage.getOrder();
    const payers = [
      {
        ...order.payment.payers[0],
        user: customer,
      },
    ];
    order.payment.payers = payers;
    OrderStorage.setOrder(order);
  }

  static getGiftValue(gift, cartValue, deliveryValue = 0, cartItems = []) {
    let baseValue = OrderStorage.typeIs(gift, GIFT_TYPE.DELIVERY)
      ? deliveryValue
      : OrderStorage.typeIs(gift, GIFT_TYPE.CART)
        ? cartValue
        : 0;
    let v = !this.typeIs(gift, GIFT_TYPE.CASHBACK_USE)
      ? gift.value || 0
      : gift.amount || 0;
    let finalGiftValue = baseValue;

    if (OrderStorage.typeIs(gift, GIFT_TYPE.PRODUCT)) {
      const coupon = BenefitStorage.getSelectedBenefit();

      if (coupon?.coupon == gift.id) {
        finalGiftValue = Benefit.getProductDiscountValue(coupon, cartItems);
      } else {
        // TODO: Consultar o cupom pela api pública de benefícios
      }
    } else if (OrderStorage.typeIs(gift, GIFT_TYPE.PERCENT)) {
      finalGiftValue = (baseValue * Math.min(v, 100)) / 100;
    } else if (OrderStorage.typeIs(gift, GIFT_TYPE.PRICE)) {
      finalGiftValue = !OrderStorage.typeIs(gift, GIFT_TYPE.ADDITION)
        ? !OrderStorage.typeIs(gift, GIFT_TYPE.CASHBACK_USE)
          ? Math.min(baseValue, v)
          : v >= baseValue
            ? baseValue > 0
              ? OMath.sum(baseValue, -1)
              : baseValue
            : v
        : v;
    }

    return +OMath.times(
      Math.abs(finalGiftValue),
      OrderStorage.typeIs(gift, GIFT_TYPE.ADDITION) ? 1 : -1,
    ).toFixed(2);
  }

  static hasFreeDelivery(order) {
    const freeDelivery = EnvironmentStorage.getFreeDelivery();
    const subtotal = Number(order?.cart?.total || 0);

    return freeDelivery?.minOrder > 0 && subtotal >= freeDelivery.minOrder;
  }

  static adjustTotal(order) {
    const { gift } = order;
    const cartTotal = Number(order?.cart?.total || 0);
    const deliveryTotal = Number(order?.delivery?.total || 0);
    const cartItems = order?.cart?.catalog?.items || [];

    const hasGift = !!gift;
    const isCashbackEarn =
      hasGift && OrderStorage.typeIs(gift, GIFT_TYPE.CASHBACK_EARN);

    if (hasGift) {
      if (OrderStorage.typeIs(gift, GIFT_TYPE.DELIVERY)) {
        gift.value = deliveryTotal;
      }

      gift.total = OrderStorage.getGiftValue(
        gift,
        cartTotal,
        deliveryTotal,
        cartItems,
      );

      if (this.typeIs(gift, GIFT_TYPE.CASHBACK_USE)) {
        gift.value = OMath.times(gift.total, -1);
      }

      order.gift = gift;
    }

    const hasFreeDelivery = OrderStorage.hasFreeDelivery({
      ...order,
      cart: { ...order.cart, total: cartTotal },
    });

    order.delivery.discount = hasFreeDelivery ? deliveryTotal : 0;

    const finalDelivery = OMath.sub(deliveryTotal, order.delivery.discount);

    order.total = +OMath.sum(
      cartTotal,
      finalDelivery,
      isCashbackEarn ? 0 : order.gift?.total || 0,
    ).toFixed(2);

    return order;
  }
}
